import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Send, Trash2, Upload } from 'lucide-react'
import { g100ConsoleApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { TypedNameDialog } from '@/components/g100Console/TypedNameDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/lib/toast'
import type { G100ConsoleSendRequest, G100Firmware, G100FirmwareChannel } from '@/types'

// #399 — the Firmware card: store firmware files and send one to this camera as an otaUpdate.
// The camera downloads the file from the device host and reports its progress under Results.

export const FIRMWARE_MAX_BYTES = 64 * 1024 * 1024

const CHANNELS: G100FirmwareChannel[] = [1, 2, 3]

/** The server's reason for a refusal, in one line. */
function reason(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: Record<string, unknown>; detail?: unknown } } })
    ?.response?.data
  if (data?.errors) return Object.entries(data.errors).map(([k, v]) => `${k}: ${String(v)}`).join('; ')
  if (data?.detail) return String(data.detail)
  return ''
}

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status

const field = 'rounded border border-border bg-background px-2 py-1 text-sm'

export function FirmwareCard({ cameraId, cameraName }: { cameraId: number; cameraName: string }) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  const list = useQuery({
    queryKey: queryKeys.g100Console.firmware(),
    queryFn: () => g100ConsoleApi.firmware(),
  })
  const [file, setFile] = useState<File | null>(null)
  const [fileKey, setFileKey] = useState(0)      // remounts the file input to clear it
  const [channel, setChannel] = useState<G100FirmwareChannel>(2)
  const [version, setVersion] = useState('')
  const [note, setNote] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<G100ConsoleSendRequest | null>(null)
  const [sending, setSending] = useState<G100Firmware | null>(null)

  const throttledOr = (error: unknown) => (statusOf(error) === 429 ? t('actions.throttled') : reason(error))

  const upload = useMutation({
    mutationFn: (body: { file: File; channel: G100FirmwareChannel; version: string; note: string }) =>
      g100ConsoleApi.uploadFirmware(body),
    onSuccess: (row) => {
      setFile(null)
      setFileKey((k) => k + 1)
      setVersion('')
      setNote('')
      setUploadError(null)
      toast.success(t('firmware.upload.uploaded', { version: row.version }))
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.firmware() })
    },
    onError: (error) => {
      const why = throttledOr(error)
      setUploadError(why ? t('firmware.upload.refused', { reason: why }) : t('firmware.upload.failed'))
    },
  })

  const remove = useMutation({
    mutationFn: (row: G100Firmware) => g100ConsoleApi.deleteFirmware(row.id),
    onSuccess: (_void, row) => {
      toast.success(t('firmware.deleted', { version: row.version }))
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.firmware() })
    },
    onError: (error) => toast.error(t('firmware.deleteFailed', { reason: throttledOr(error) })),
  })

  const send = useMutation({
    mutationFn: ({ row, name }: { row: G100Firmware; name: string }) =>
      g100ConsoleApi.sendFirmware(cameraId, row.id, name),
    onSuccess: (_row, { row }) => {
      setConfirming(null)
      setSending(null)
      toast.success(t('firmware.sent', { version: row.version, camera: cameraName }))
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.commands(cameraId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.messagesAll(cameraId) })
    },
    onError: (error) => toast.error(t('firmware.sendFailed', { reason: throttledOr(error) })),
  })

  const onUpload = () => {
    if (!file) return
    if (file.size > FIRMWARE_MAX_BYTES) {
      setUploadError(t('firmware.upload.tooBig', { size: file.size, max: FIRMWARE_MAX_BYTES }))
      return
    }
    setUploadError(null)
    upload.mutate({ file, channel, version: version.trim(), note: note.trim() })
  }

  const rows = list.data?.results ?? []

  return (
    <section aria-label={t('firmware.title')}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('firmware.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('firmware.hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3 rounded border border-border/60 p-2">
            <div className="space-y-1">
              <label htmlFor="g100-firmware-file" className="block text-xs font-medium">{t('firmware.upload.file')}</label>
              <input
                key={fileKey}
                id="g100-firmware-file"
                type="file"
                onChange={(e) => { setFile(e.target.files?.[0] ?? null); setUploadError(null) }}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="g100-firmware-channel" className="block text-xs font-medium">{t('firmware.upload.channel')}</label>
              <select
                id="g100-firmware-channel"
                value={channel}
                onChange={(e) => setChannel(Number(e.target.value) as G100FirmwareChannel)}
                className={field}
              >
                {CHANNELS.map((c) => <option key={c} value={c}>{t(`firmware.channels.${c}`)}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="g100-firmware-version" className="block text-xs font-medium">{t('firmware.upload.version')}</label>
              <input id="g100-firmware-version" value={version} maxLength={64}
                onChange={(e) => setVersion(e.target.value)} autoComplete="off" className={field} />
            </div>
            <div className="space-y-1">
              <label htmlFor="g100-firmware-note" className="block text-xs font-medium">{t('firmware.upload.note')}</label>
              <input id="g100-firmware-note" value={note} maxLength={200}
                onChange={(e) => setNote(e.target.value)} autoComplete="off" className={field} />
            </div>
            <Button size="sm" onClick={onUpload} disabled={!file || version.trim() === '' || upload.isPending}>
              {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {t('firmware.upload.button')}
            </Button>
          </div>
          {uploadError && <p role="alert" className="text-xs text-destructive">{uploadError}</p>}

          {list.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : list.isError ? (
            <p className="text-sm text-destructive">{t('firmware.loadError')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('firmware.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    <th className="pr-3 font-medium">{t('firmware.table.channel')}</th>
                    <th className="pr-3 font-medium">{t('firmware.table.version')}</th>
                    <th className="pr-3 font-medium">{t('firmware.table.size')}</th>
                    <th className="pr-3 font-medium">{t('firmware.table.uploaded')}</th>
                    <th className="pr-3 font-medium">{t('firmware.table.by')}</th>
                    <th className="pr-3 font-medium">{t('firmware.table.note')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-border/60 align-top">
                      <td className="py-1 pr-3">{t(`firmware.channels.${row.channel}`)}</td>
                      <td className="py-1 pr-3 font-mono">{row.version}</td>
                      <td className="py-1 pr-3">{row.size}</td>
                      <td className="py-1 pr-3">{new Date(row.uploaded_at).toLocaleString()}</td>
                      <td className="py-1 pr-3">{row.uploaded_by ?? ''}</td>
                      <td className="py-1 pr-3">{row.note}</td>
                      <td className="flex flex-wrap gap-2 py-1">
                        <Button size="sm" variant="outline" onClick={() => remove.mutate(row)} disabled={remove.isPending}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('firmware.delete')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSending(row)
                            setConfirming({
                              action: 'otaUpdate',
                              params: { firmware_id: row.id, channel: row.channel, version: row.version, size: row.size },
                            })
                          }}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {t('firmware.send', { camera: cameraName })}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Remounted on open and close, so the typed name never carries over. */}
        <TypedNameDialog
          key={confirming ? 'open' : 'closed'}
          request={confirming}
          cameraName={cameraName}
          sending={send.isPending}
          onCancel={() => { setConfirming(null); setSending(null) }}
          onConfirm={(name) => sending && send.mutate({ row: sending, name })}
        />
      </Card>
    </section>
  )
}
